import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, X, User, Network, Wifi } from 'lucide-react';
import { useCall } from '@/contexts/CallContext';
import { MemberAvatar } from '@/components/MemberAvatar';

function formatDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function CallOverlay() {
  const call = useCall();
  if (!call) return null;
  const { callState, peer } = call;

  if (callState === 'incoming') return <IncomingCallSheet />;
  if (callState === 'ringing' || callState === 'connecting' || callState === 'active') {
    return <CallWindow />;
  }
  return null;
}

function IncomingCallSheet() {
  const { peer, acceptCall, rejectCall } = useCall();
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-fade-in" data-testid="incoming-call-modal">
      <div className="bg-white rounded-sm border border-slate-200 shadow-2xl w-[360px] overflow-hidden">
        <div className="bg-gradient-to-b from-[#0047AB] to-[#003580] text-white px-6 py-8 text-center">
          <div className="flex justify-center mb-3">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
              <div className="relative">
                <MemberAvatar userId={peer?.id} name={peer?.name} size="lg" />
              </div>
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] opacity-80 mb-1">Incoming video call</p>
          <h3 className="font-heading text-xl font-bold" data-testid="incoming-call-name">{peer?.name || 'Unknown'}</h3>
          <p className="text-xs opacity-70 mt-0.5">{peer?.email}</p>
        </div>
        <div className="p-5 flex items-center justify-around bg-white">
          <button
            onClick={rejectCall}
            className="flex flex-col items-center gap-1.5 group"
            data-testid="reject-call-btn"
            aria-label="Decline call"
          >
            <span className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-transform group-active:scale-95">
              <PhoneOff className="w-5 h-5" />
            </span>
            <span className="text-[11px] font-medium text-slate-600">Decline</span>
          </button>
          <button
            onClick={acceptCall}
            className="flex flex-col items-center gap-1.5 group"
            data-testid="accept-call-btn"
            aria-label="Accept call"
          >
            <span className="w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center shadow-md transition-transform group-active:scale-95 animate-pulse">
              <Phone className="w-5 h-5" />
            </span>
            <span className="text-[11px] font-medium text-slate-600">Accept</span>
          </button>
        </div>
      </div>
      <style>{`@keyframes fade-in {from{opacity:0}to{opacity:1}} .animate-fade-in{animation:fade-in 0.18s ease-out}`}</style>
    </div>
  );
}

function CallWindow() {
  const {
    callState, peer, isMuted, isCameraOff, callStartedAt, transport,
    localStreamRef, remoteStreamRef,
    endCall, rejectCall, toggleMute, toggleCamera,
  } = useCall();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [now, setNow] = useState(Date.now());

  // Attach streams to <video>
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  });
  useEffect(() => {
    if (remoteVideoRef.current && remoteStreamRef.current) {
      remoteVideoRef.current.srcObject = remoteStreamRef.current;
    }
  });

  // Tick for call duration
  useEffect(() => {
    if (callState !== 'active') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [callState]);

  const statusLabel = callState === 'ringing'
    ? 'Ringing…'
    : callState === 'connecting'
      ? 'Connecting…'
      : (callStartedAt ? formatDuration(now - callStartedAt) : '00:00');

  return (
    <div className="fixed bottom-4 right-4 z-[100] w-[420px] max-w-[calc(100vw-2rem)] bg-slate-900 rounded-sm border border-slate-700 shadow-2xl overflow-hidden" data-testid="call-window">
      {/* Header */}
      <div className="px-4 py-2.5 bg-slate-950 flex items-center justify-between border-b border-slate-800">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate" data-testid="call-peer-name">{peer?.name || 'Teammate'}</p>
            <p className="text-[10px] text-slate-400 font-mono" data-testid="call-status">{statusLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {transport && callState === 'active' && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider ${
                transport === 'relay'
                  ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                  : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
              }`}
              title={transport === 'relay'
                ? 'Traffic is being routed through a TURN relay (slightly higher latency).'
                : 'Direct peer-to-peer connection (lowest latency).'}
              data-testid="call-transport-pill"
            >
              {transport === 'relay' ? <Network className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
              {transport === 'relay' ? 'Relay' : 'Direct'}
            </span>
          )}
          <button
            onClick={callState === 'ringing' ? rejectCall : endCall}
            className="text-slate-400 hover:text-white p-1 rounded-sm hover:bg-slate-800"
            aria-label="Close call window"
            data-testid="call-window-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Video area */}
      <div className="relative bg-slate-800 aspect-video">
        {callState === 'active' ? (
          <>
            {/* Remote takes full area */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover bg-slate-900"
              data-testid="remote-video"
            />
            {/* Local picture-in-picture */}
            <div className="absolute bottom-2 right-2 w-24 h-32 rounded-sm overflow-hidden border-2 border-white/20 shadow-lg bg-slate-900">
              {isCameraOff ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-400">
                  <VideoOff className="w-5 h-5" />
                </div>
              ) : (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  data-testid="local-video"
                />
              )}
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <div className="relative mb-3">
              <div className="absolute inset-0 rounded-full bg-[#0047AB]/40 animate-ping" />
              <div className="relative w-16 h-16 rounded-full bg-[#0047AB] flex items-center justify-center">
                <User className="w-7 h-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-medium text-white">{peer?.name || 'Teammate'}</p>
            <p className="text-xs text-slate-400 mt-1">{statusLabel}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-slate-950 px-4 py-3 flex items-center justify-center gap-3">
        <button
          onClick={toggleMute}
          disabled={callState !== 'active'}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            isMuted ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-white'
          }`}
          aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
          data-testid="toggle-mute-btn"
        >
          {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <button
          onClick={toggleCamera}
          disabled={callState !== 'active'}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            isCameraOff ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-slate-800 hover:bg-slate-700 text-white'
          }`}
          aria-label={isCameraOff ? 'Turn camera on' : 'Turn camera off'}
          data-testid="toggle-camera-btn"
        >
          {isCameraOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
        </button>
        <button
          onClick={callState === 'ringing' ? rejectCall : endCall}
          className="w-12 h-11 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
          aria-label="End call"
          data-testid="end-call-btn"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
